import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { DollarSign, CheckCircle, RefreshCw, RotateCcw, Loader2 } from 'lucide-react';
import {
  listPayouts,
  markPayoutPaid,
  syncPlanRevenue,
  uncommitPlan,
} from '../lib/commissionActions';

const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const STATUS_STYLES = {
  paid: 'bg-emerald-50 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  partial: 'bg-blue-50 dark:bg-blue-600/20 text-blue-700 dark:text-blue-300 border-blue-500/30',
  pending: 'bg-amber-50 dark:bg-amber-600/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
};

/**
 * Post-commit commission panel: lists the per-user payout records for a
 * committed plan and lets finance settle them on-screen (mark paid), refresh
 * the revenue (sync), or undo the commit (uncommit).
 *
 * Props:
 *   commissionId — the committed plan's id (a deal's commission_id)
 *   onChanged    — called after uncommit so the parent can reload deals
 *   canManage    — whether the current user may settle/undo (defaults true)
 */
export default function CommissionPayoutsPanel({ commissionId, onChanged, canManage = true }) {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_owed: 0 });
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState(null); // payout id currently being paid
  const [payAmounts, setPayAmounts] = useState({}); // per-row partial amount (id -> string)
  const [actionBusy, setActionBusy] = useState(null); // 'sync' | 'uncommit'

  const load = useCallback(async () => {
    if (!commissionId) return;
    setLoading(true);
    try {
      const data = await listPayouts(commissionId);
      setPayouts(data.payouts || []);
      setSummary({ count: data.count || 0, total_owed: data.total_owed || 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [commissionId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPaid = payouts.reduce((s, p) => s + (Number(p.amount_paid) || 0), 0);

  const handleMarkPaid = async (payout) => {
    // Use the typed partial amount if present; otherwise pay the full remaining
    // balance (amount omitted -> backend settles the remainder).
    const raw = payAmounts[payout.commission_id];
    let amount = null;
    if (raw != null && String(raw).trim() !== '') {
      amount = parseFloat(raw);
      const remaining = (Number(payout.amount_owed) || 0) - (Number(payout.amount_paid) || 0);
      if (!(amount > 0)) {
        toast.error('Enter an amount greater than 0');
        return;
      }
      if (amount > remaining + 0.005) {
        toast.error(`Amount exceeds the ${money(remaining)} remaining`);
        return;
      }
    }
    setRowBusy(payout.commission_id);
    try {
      const res = await markPayoutPaid(payout.commission_id, amount);
      toast.success(
        res.status === 'paid'
          ? `Paid in full — ${money(res.amount_paid)}`
          : `Payment recorded — ${money(res.amount_paid)} of ${money(res.amount_owed)}`,
      );
      setPayAmounts((m) => {
        const next = { ...m };
        delete next[payout.commission_id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRowBusy(null);
    }
  };

  const handleSync = async () => {
    setActionBusy('sync');
    try {
      const res = await syncPlanRevenue(commissionId);
      toast.success(`Revenue synced — ${money(res.revenue)}`);
      await load();
    } catch (err) {
      // A committed plan is locked; the backend explains why.
      toast.error(err.message);
    } finally {
      setActionBusy(null);
    }
  };

  const handleUncommit = async () => {
    if (
      !window.confirm(
        'Undo this commission? Pending payouts will be removed and the deal returns to “Needs Commission”. This is blocked if any payout has been paid.',
      )
    ) {
      return;
    }
    setActionBusy('uncommit');
    try {
      const res = await uncommitPlan(commissionId);
      toast.success(`Commission undone — ${res.payouts_removed} payout(s) removed`);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="bg-boss-accent-muted rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-boss-text-secondary flex items-center gap-1.5">
          <DollarSign size={14} className="text-violet-600 dark:text-violet-400" />
          Commission payouts
        </h4>
        {!loading && (
          <span className="text-[10px] text-boss-text-muted">
            {summary.count} payout{summary.count === 1 ? '' : 's'} · {money(totalPaid)} paid of{' '}
            {money(summary.total_owed)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-boss-text-muted py-3">
          <Loader2 size={14} className="animate-spin" /> Loading payouts…
        </div>
      ) : payouts.length === 0 ? (
        <p className="text-xs text-boss-text-muted py-2">
          No payouts on this plan yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-boss-text-muted text-left">
                <th className="font-medium py-1 pr-2">Recipient</th>
                <th className="font-medium py-1 pr-2">Tier</th>
                <th className="font-medium py-1 pr-2 text-right">Owed</th>
                <th className="font-medium py-1 pr-2 text-right">Paid</th>
                <th className="font-medium py-1 pr-2">Status</th>
                <th className="font-medium py-1" />
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.commission_id} className="border-t border-boss-border/60">
                  <td className="py-1.5 pr-2 text-boss-text">{p.user_name || p.user_id}</td>
                  <td className="py-1.5 pr-2 text-boss-text-secondary">{p.tier_name || `Tier ${p.tier_number}`}</td>
                  <td className="py-1.5 pr-2 text-right text-boss-text">{money(p.amount_owed)}</td>
                  <td className="py-1.5 pr-2 text-right text-boss-text-secondary">{money(p.amount_paid)}</td>
                  <td className="py-1.5 pr-2">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded border text-[10px] capitalize ${
                        STATUS_STYLES[p.status] || STATUS_STYLES.pending
                      }`}
                    >
                      {p.status || 'pending'}
                    </span>
                  </td>
                  <td className="py-1.5 text-right">
                    {canManage && p.status !== 'paid' && (
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <div className="relative">
                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-boss-text-muted pointer-events-none">
                            $
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={payAmounts[p.commission_id] ?? ''}
                            onChange={(e) =>
                              setPayAmounts((m) => ({ ...m, [p.commission_id]: e.target.value }))
                            }
                            placeholder={String(
                              ((Number(p.amount_owed) || 0) - (Number(p.amount_paid) || 0)).toFixed(2),
                            )}
                            title="Amount to pay now — leave blank to pay the full remaining balance"
                            className="w-20 pl-4 pr-1.5 py-1 bg-boss-elevated border border-boss-border rounded text-[11px] text-boss-text text-right outline-none focus:border-emerald-500"
                          />
                        </div>
                        <button
                          onClick={() => handleMarkPaid(p)}
                          disabled={rowBusy === p.commission_id}
                          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] px-2 py-1 rounded transition disabled:opacity-50"
                        >
                          {rowBusy === p.commission_id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <CheckCircle size={11} />
                          )}
                          {payAmounts[p.commission_id] && String(payAmounts[p.commission_id]).trim() !== ''
                            ? 'Pay'
                            : 'Mark paid'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && (
        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-boss-border/60">
          <button
            onClick={handleSync}
            disabled={actionBusy !== null}
            className="inline-flex items-center gap-1.5 bg-boss-elevated hover:bg-boss-surface text-boss-text text-[11px] px-2.5 py-1.5 rounded transition disabled:opacity-50"
            title="Refresh revenue from delivered work (available after undo — a committed plan is locked)"
          >
            {actionBusy === 'sync' ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            Sync revenue
          </button>
          <button
            onClick={handleUncommit}
            disabled={actionBusy !== null}
            className="inline-flex items-center gap-1.5 bg-boss-elevated hover:bg-boss-surface text-boss-text text-[11px] px-2.5 py-1.5 rounded transition disabled:opacity-50"
          >
            {actionBusy === 'uncommit' ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RotateCcw size={12} />
            )}
            Undo commit
          </button>
        </div>
      )}
    </div>
  );
}
